import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CanAccess, CustomRoutes, localStorageStore, Resource } from "ra-core";
import {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
  Building2,
  Camera,
  Clapperboard,
  FileText,
  Film,
  Flag,
  Inbox,
  Mail,
  Link2,
  MessageSquareWarning,
  QrCode,
  RadioTower,
} from "lucide-react";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";
import { AuthVerifyPage } from "@/components/supabase/auth-verify-page";
import { shouldPersistQuery } from "./persistQueryFilter";

import { agencies } from "../agencies";
import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import echoflicks, { echoes } from "../echoflicks";
import { featureCohorts } from "../featureCohorts";
import { subscribers } from "../subscribers";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import { WhatsAppBroadcastsPage } from "../whatsapp/WhatsAppBroadcastsPage.tsx";
import { WhatsAppInboxPage } from "../whatsapp/inbox/WhatsAppInboxPage.tsx";
import { TePoneCodesPage } from "../tepone/TePoneCodesPage.tsx";
import { GuerillaPage } from "../guerilla/GuerillaPage.tsx";
import { EchoFeedbackPage } from "../feedback/EchoFeedbackPage.tsx";
import { PosterEmailsList } from "../echoflicks/PosterEmailsList.tsx";
import { WhatsAppBroadcastAnalyticsPage } from "../whatsapp/WhatsAppBroadcastAnalyticsPage.tsx";
import { WhatsAppTemplatesPage } from "../whatsapp/WhatsAppTemplatesPage.tsx";
import { FunnelAnalyticsPage } from "../funnel/FunnelAnalyticsPage.tsx";
import { ExperimentsPage } from "../experiments/ExperimentsPage.tsx";
import { MonetizationPage } from "../monetization/MonetizationPage.tsx";
import { AiSettingsPage } from "../ai-settings/AiSettingsPage.tsx";
import { H3LocalPage } from "../experiments/H3LocalPage";
import { LabResultShowPage, LabResultsPage } from "../lab-results";
import { GpuFleetPage } from "../gpu-fleet/GpuFleetPage.tsx";
import { LlmConfigPage } from "../llm-config/LlmConfigPage.tsx";
import { FeatureFlagsPage } from "../feature-flags/FeatureFlagsPage.tsx";
import { LabsPage } from "../labs/LabsPage.tsx";
import { TrackingLinksPage } from "../trackingLinks/TrackingLinksPage.tsx";
import { TrackingLinkAnalyticsPage } from "../trackingLinks/TrackingLinkAnalyticsPage.tsx";
import { SharingPage } from "../sharing/SharingPage.tsx";
import { SoundPage } from "../sound/SoundPage.tsx";
import { QueuePage } from "../queue/QueuePage";
import { IncidentsPage } from "../incidents/IncidentsPage";
import { adminOnly } from "./adminOnly";
// Three.js and the replay UI load only when an administrator opens Echo Studio.
const EchoStudioPage = lazy(() => import("../echo-studio/EchoStudioPage"));
const TvPage = lazy(() => import("../tv/TvPage"));
// Marketing & acquisition (v1, v2 and v3) is its own chunk: its charts,
// tables and motion load only when an administrator opens the page, never
// with the app's entry bundle (UX-61; scripts/check-bundle-budget.mjs).
const MarketingPage = lazy(() =>
  import("../marketing/MarketingPage.tsx").then((module) => ({
    default: module.MarketingPage,
  })),
);
const MarketingLegacyPage = lazy(() =>
  import("../marketing/v2/MarketingLegacyPage.tsx").then((module) => ({
    default: module.MarketingLegacyPage,
  })),
);
// Gallery usage (my.echoflicks.com adoption) is its own chunk too.
const GalleryUsagePage = lazy(() =>
  import("../gallery-usage/GalleryUsagePage.tsx").then((module) => ({
    default: module.GalleryUsagePage,
  })),
);
// RE funnel (real-estate leads from signup link to first film) is its own
// chunk: it loads only when an administrator opens the page (bundle budget).
const ReFunnelPage = lazy(() =>
  import("../re-funnel/ReFunnelPage.tsx").then((module) => ({
    default: module.ReFunnelPage,
  })),
);
// Outreach (first film + templated email + tracking, plan 2026-10-20) is its
// own chunk: the pipeline page and the Template studio load only when an
// administrator opens them.
const OutreachPage = lazy(() =>
  import("../outreach/OutreachPage.tsx").then((module) => ({
    default: module.OutreachPage,
  })),
);
const TemplateStudioPage = lazy(() =>
  import("../outreach/TemplateStudio.tsx").then((module) => ({
    default: module.TemplateStudioPage,
  })),
);
const outreachFallback = (
  <p className="p-6 text-sm text-muted-foreground" aria-busy="true">
    Loading Outreach…
  </p>
);
const reFunnelFallback = (
  <p className="p-6 text-sm text-muted-foreground" aria-busy="true">
    Loading RE funnel…
  </p>
);
// Costs (live ledger, owned GPUs, the capacity simulator) is its own chunk:
// it loads only when an administrator opens the page. The route component
// adds the Studio directory (agency names for the Studio music block).
const CostsPage = lazy(() =>
  import("../costs/CostsPage.tsx").then((module) => ({
    default: module.CostsRoute,
  })),
);
const costsFallback = (
  <p className="p-6 text-sm text-muted-foreground" aria-busy="true">
    Loading Costs…
  </p>
);
const galleryUsageFallback = (
  <p className="p-6 text-sm text-muted-foreground" aria-busy="true">
    Loading Gallery usage…
  </p>
);
const MARKETING_PATH = "/marketing";
const MARKETING_LEGACY_PATH = "/marketing/legacy";
const marketingFallback = (
  <p className="p-6 text-sm text-muted-foreground" aria-busy="true">
    Loading Marketing &amp; acquisition…
  </p>
);
import { NoahKyraIdentityProofPage } from "../identityProof/NoahKyraIdentityProofPage.tsx";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  googleWorkplaceDomain = import.meta.env.VITE_GOOGLE_WORKPLACE_DOMAIN,
  disableEmailPasswordAuthentication = import.meta.env
    .VITE_DISABLE_EMAIL_PASSWORD_AUTHENTICATION === "true",
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
        googleWorkplaceDomain,
        disableEmailPasswordAuthentication,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

type ResponsiveAdminProps = CoreAdminProps & {
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
};

/**
 * Cache policy shared by both admins. A record or list fetched in the last two
 * minutes is served from memory when the user navigates back to it (instant
 * page), then refetched only once it is older than that; nothing refetches
 * merely because the window regained focus — the live surfaces (inbox, chat)
 * poll on their own interval, and ra-core still invalidates after every
 * mutation. Without this ra-core's default client treats everything as stale
 * on mount, so each contact visit repeated the full set of requests.
 */
const QUERY_STALE_MS = 2 * 60 * 1000;
const QUERY_GC_MS = 30 * 60 * 1000;

const createCrmQueryClient = (
  overrides: {
    gcTime?: number;
    networkMode?: "online" | "always" | "offlineFirst";
  } = {},
) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_MS,
        gcTime: overrides.gcTime ?? QUERY_GC_MS,
        refetchOnWindowFocus: false,
        ...(overrides.networkMode
          ? { networkMode: overrides.networkMode }
          : {}),
      },
      mutations: overrides.networkMode
        ? { networkMode: overrides.networkMode }
        : {},
    },
  });

const configurationOnly = (page: ReactNode) => (
  <CanAccess resource="configuration" action="edit">
    {page}
  </CanAccess>
);

// Route and resource lists are plain arrays so the desktop and phone admins
// register the same destinations from one definition. ra-core only recognises
// <Resource>/<CustomRoutes> elements as direct children of <Admin>, and
// React.Children flattens arrays but not fragments, hence arrays with keys.

/** Sign-up / password / OAuth pages rendered outside the app layout. */
const authenticationRoutes = () => [
  <Route key="signup" path={SignupPage.path} element={<SignupPage />} />,
  <Route
    key="confirmation"
    path={ConfirmationRequired.path}
    element={<ConfirmationRequired />}
  />,
  <Route
    key="set-password"
    path={SetPasswordPage.path}
    element={<SetPasswordPage />}
  />,
  <Route
    key="verify"
    path={AuthVerifyPage.path}
    element={<AuthVerifyPage />}
  />,
  <Route
    key="forgot-password"
    path={ForgotPasswordPage.path}
    element={<ForgotPasswordPage />}
  />,
  <Route
    key="oauth-consent"
    path={OAuthConsentPage.path}
    element={<OAuthConsentPage />}
  />,
];

/** Analytics and tooling pages reachable from the navigation on every device. */
const workspaceRoutes = () => [
  <Route
    key="queue"
    path={QueuePage.path}
    element={
      <CanAccess resource="pipeline_queue" action="list">
        <QueuePage />
      </CanAccess>
    }
  />,
  <Route
    key="echo-incidents"
    path={IncidentsPage.path}
    element={
      // Stuck-Echo incidents carry customer names and chat links: the proxy
      // and the dashboard API enforce the same administrator boundary.
      <CanAccess resource="echo_incidents" action="list">
        <IncidentsPage />
      </CanAccess>
    }
  />,
  <Route
    key="echoflicks-tv"
    path="/tv/*"
    element={
      <Suspense fallback={<p className="p-6">Loading TV…</p>}>
        <TvPage />
      </Suspense>
    }
  />,
  <Route
    key="echo-studio"
    path="/echo-studio/:workloadId?"
    element={
      <CanAccess resource="echo_studio" action="list">
        <Suspense
          fallback={
            <p className="p-6 text-sm text-muted-foreground">
              Loading Echo Studio…
            </p>
          }
        >
          <EchoStudioPage />
        </Suspense>
      </CanAccess>
    }
  />,
  <Route
    key="changelog"
    path={ChangelogPage.path}
    element={<ChangelogPage />}
  />,
  // Campaign spend and acquisition across every contact: administrators only
  // (MKT2-62). `/marketing` is v3 or v2 per the runtime policy
  // (`ui.page_version`, admins can `?preview=v3`); v2 falls back to the v1
  // page while its API answers 503. `/marketing/legacy` keeps v1.
  <Route
    key="marketing"
    path={MARKETING_PATH}
    element={adminOnly(
      <Suspense fallback={marketingFallback}>
        <MarketingPage />
      </Suspense>,
    )}
  />,
  <Route
    key="marketing-legacy"
    path={MARKETING_LEGACY_PATH}
    element={adminOnly(
      <Suspense fallback={marketingFallback}>
        <MarketingLegacyPage />
      </Suspense>,
    )}
  />,
  <Route
    key="funnel"
    path={FunnelAnalyticsPage.path}
    element={<FunnelAnalyticsPage />}
  />,
  <Route
    key="h3-local-experiment"
    path={H3LocalPage.path}
    element={adminOnly(<H3LocalPage />)}
  />,
  <Route
    key="experiments"
    path={ExperimentsPage.path}
    element={adminOnly(<ExperimentsPage />)}
  />,
  <Route
    key="lab-results"
    path={LabResultsPage.path}
    element={<LabResultsPage />}
  />,
  <Route
    key="lab-result"
    path={LabResultShowPage.path}
    element={<LabResultShowPage />}
  />,
  <Route
    key="monetization"
    path={MonetizationPage.path}
    element={adminOnly(<MonetizationPage />)}
  />,
  <Route
    key="costs"
    path="/costs"
    element={adminOnly(
      <Suspense fallback={costsFallback}>
        <CostsPage />
      </Suspense>,
    )}
  />,
  <Route
    key="gallery-usage"
    path="/gallery-usage"
    element={adminOnly(
      <Suspense fallback={galleryUsageFallback}>
        <GalleryUsagePage />
      </Suspense>,
    )}
  />,
  // Real-estate leads by e-mail, replay links and agency films: the second
  // and third gates are the proxy's administrator list and the API's
  // operator-admin check (plan 2026-10-08 G-19; /funnel lacks them).
  <Route
    key="re-funnel"
    path="/re-funnel"
    element={adminOnly(
      <Suspense fallback={reFunnelFallback}>
        <ReFunnelPage />
      </Suspense>,
    )}
  />,
  // Outreach: agent names and e-mails, films made as staff and email sends.
  // Administrators only here (`outreach`), again in the edge function
  // (`administrator_required`) and in the RPCs.
  <Route
    key="outreach"
    path="/outreach"
    element={
      <CanAccess resource="outreach" action="list">
        <Suspense fallback={outreachFallback}>
          <OutreachPage />
        </Suspense>
      </CanAccess>
    }
  />,
  <Route
    key="outreach-template"
    path="/outreach/templates/:templateId"
    element={
      <CanAccess resource="outreach" action="list">
        <Suspense fallback={outreachFallback}>
          <TemplateStudioPage />
        </Suspense>
      </CanAccess>
    }
  />,
  <Route
    key="ai-settings"
    path={AiSettingsPage.path}
    element={<AiSettingsPage />}
  />,
  <Route
    key="gpu-fleet"
    path={GpuFleetPage.path}
    element={configurationOnly(<GpuFleetPage />)}
  />,
  <Route
    key="llm-config"
    path={LlmConfigPage.path}
    element={configurationOnly(<LlmConfigPage />)}
  />,
  <Route
    key="feature-flags"
    path={FeatureFlagsPage.path}
    element={<FeatureFlagsPage />}
  />,
  <Route
    key="sound"
    path={SoundPage.path}
    element={configurationOnly(<SoundPage />)}
  />,
  <Route
    key="tracking-links"
    path={TrackingLinksPage.path}
    element={<TrackingLinksPage />}
  />,
  <Route
    key="tracking-link-analytics"
    path={TrackingLinkAnalyticsPage.path}
    element={<TrackingLinkAnalyticsPage />}
  />,
  <Route
    key="sharing"
    path={SharingPage.path}
    element={adminOnly(<SharingPage />)}
  />,
  <Route
    key="sharing-legacy"
    path={SharingPage.legacyPath}
    element={<Navigate to={SharingPage.path} replace />}
  />,
  <Route
    key="labs"
    path={LabsPage.path}
    element={configurationOnly(<LabsPage />)}
  />,
  <Route
    key="identity-proof"
    path={NoahKyraIdentityProofPage.path}
    element={<NoahKyraIdentityProofPage />}
  />,
];

/** Resources whose list/show pages are identical on desktop and phone. */
const sharedResources = () => [
  <Resource
    key="whatsapp_inbox"
    name="whatsapp_inbox"
    list={WhatsAppInboxPage}
    icon={Inbox}
  />,
  <Resource
    key="whatsapp_templates"
    name="whatsapp_templates"
    list={WhatsAppTemplatesPage}
    icon={FileText}
  />,
  <Resource
    key="whatsapp_broadcasts"
    name="whatsapp_broadcasts"
    list={WhatsAppBroadcastsPage}
    icon={RadioTower}
  >
    <Route path=":id/analytics" element={<WhatsAppBroadcastAnalyticsPage />} />
  </Resource>,
  <Resource
    key="echoflicks_echoes"
    name="echoflicks_echoes"
    {...echoes}
    icon={Clapperboard}
  />,
  <Resource
    key="echoflicks_video_sessions"
    name="echoflicks_video_sessions"
    {...echoflicks}
    icon={Film}
  />,
  <Resource
    key="echoflicks_poster_emails"
    name="echoflicks_poster_emails"
    list={PosterEmailsList}
    icon={Mail}
  />,
  <Resource
    key="echo_feedback"
    name="echo_feedback"
    list={EchoFeedbackPage}
    icon={MessageSquareWarning}
  />,
  <Resource key="echo_saves" name="echo_saves" />,
  <Resource
    key="tepone_codes"
    name="tepone_codes"
    list={TePoneCodesPage}
    icon={QrCode}
  />,
  <Resource key="guerilla" name="guerilla" list={GuerillaPage} icon={Camera} />,
  <Resource
    key="subscribers"
    name="subscribers"
    {...subscribers}
    icon={Mail}
  />,
  <Resource
    key="feature_cohorts"
    name="feature_cohorts"
    {...featureCohorts}
    icon={Flag}
  />,
  <Resource key="feature_cohort_members" name="feature_cohort_members" />,
  <Resource
    key="feature_cohort_implications"
    name="feature_cohort_implications"
  />,
  <Resource key="marketing_metrics" name="marketing_metrics" />,
  <Resource key="tracking_links" name="tracking_links" icon={Link2} />,
  <Resource key="tracking_links_summary" name="tracking_links_summary" />,
  <Resource key="tracking_link_clicks" name="tracking_link_clicks" />,
  <Resource
    key="tracking_link_daily_metrics"
    name="tracking_link_daily_metrics"
  />,
  <Resource key="gift_codes" name="gift_codes" />,
  <Resource key="gift_redemptions" name="gift_redemptions" />,
  <Resource key="sharing_referrers" name="sharing_referrers" />,
  <Resource key="sharing_redemptions" name="sharing_redemptions" />,
  <Resource key="agencies" name="agencies" {...agencies} icon={Building2} />,
  <Resource key="agency_members" name="agency_members" />,
  <Resource key="agency_portal_users" name="agency_portal_users" />,
  <Resource key="agency_brand_profiles" name="agency_brand_profiles" />,
  <Resource key="brand_extractions" name="brand_extractions" />,
];

const DesktopAdmin = (props: ResponsiveAdminProps) => {
  const [queryClient] = useState(() => createCrmQueryClient());
  return (
    <Admin
      queryClient={queryClient}
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>{authenticationRoutes()}</CustomRoutes>

      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        {workspaceRoutes()}
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      {sharedResources()}
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
    </Admin>
  );
};

const MobileAdmin = (props: ResponsiveAdminProps) => {
  // Created once per mount: a client built on every render would throw the
  // cache away each time this component re-rendered.
  const [queryClient] = useState(() =>
    createCrmQueryClient({
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      networkMode: "offlineFirst",
    }),
  );
  const [asyncStoragePersister] = useState(() =>
    createAsyncStoragePersister({ storage: localStorage }),
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        // Bust the persisted query cache on every deploy so a stale/empty
        // cached result (e.g. from a since-fixed bug) is never rehydrated, and
        // cap how long any persisted data may be reused.
        buster: __APP_BUILD_ID__,
        maxAge: 1000 * 60 * 60, // 1 hour
        dehydrateOptions: {
          // Persist only identity/config for offline boot — never resource
          // collections/records, which go stale and caused empty-list bugs.
          shouldDehydrateQuery: shouldPersistQuery,
        },
      }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>{authenticationRoutes()}</CustomRoutes>
        <CustomRoutes>
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          {workspaceRoutes()}
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        {sharedResources()}
        <Resource name="companies" show={CompanyShow} />
        <Resource name="tasks" list={MobileTasksList} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
